import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreatePrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";

type Props = {
    open: boolean;
    onClose: () => void;
};

export const NewCommandModal: FC<Props> = ({ open, onClose }) => {
    const [name, setName] = useState("");
    const [command, setCommand] = useState("");
    const [description, setDescription] = useState("");
    const [prompt, setPrompt] = useState("");
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const createPrompt = useCreatePrompt();
    const { toast } = useToast();

    const markTouched = (field: string) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const handleSave = useCallback(async () => {
        try {
            await createPrompt.mutateAsync({
                name: name.trim(),
                description: description.trim(),
                prompt: prompt.trim(),
                isCommand: true,
                command: command.trim(),
            });
            toast({
                title: "Command created successfully",
                description: `/${command} is now available to your organization`,
            });
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "Failed to create command",
                description: formatError(error),
            });
        }
    }, [name, command, description, prompt, createPrompt, toast, onClose]);

    const isValid = name.trim() && command.trim() && description.trim() && prompt.trim();

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="new-command-modal"
                data-track-location={TrackLocations.NewCommandModal}
            >
                <DialogHeader>
                    <DialogTitle>New Agent Command</DialogTitle>
                    <DialogDescription>Create a custom command for your organization</DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    <InputField
                        id="command-name"
                        label="Name"
                        error={touched.name && !name.trim() ? "Name is required" : undefined}
                    >
                        <Input
                            id="command-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => markTouched("name")}
                            placeholder="Command name"
                            data-testid="command-name-input"
                        />
                    </InputField>

                    <InputField
                        id="command-trigger"
                        label="Command"
                        error={touched.command && !command.trim() ? "Command is required" : undefined}
                    >
                        <Input
                            id="command-trigger"
                            value={command}
                            onChange={(e) => setCommand(e.target.value)}
                            onBlur={() => markTouched("command")}
                            placeholder="trigger-word"
                            prefix="/"
                            data-testid="command-trigger-input"
                        />
                    </InputField>

                    <InputField
                        id="command-description"
                        label="Description"
                        error={touched.description && !description.trim() ? "Description is required" : undefined}
                    >
                        <Input
                            id="command-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            onBlur={() => markTouched("description")}
                            placeholder="Brief description of what this command does"
                            data-testid="command-description-input"
                        />
                    </InputField>

                    <InputField
                        id="command-prompt"
                        label="Prompt"
                        hint={`${prompt.length.toLocaleString()} / ${(20000).toLocaleString()} characters`}
                        error={touched.prompt && !prompt.trim() ? "Prompt is required" : undefined}
                    >
                        <Textarea
                            id="command-prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onBlur={() => markTouched("prompt")}
                            placeholder="The prompt that will be sent to the AI agent"
                            minHeight={120}
                            maxHeight={300}
                            className="resize-none"
                            data-testid="command-prompt-textarea"
                            maxLength={20000}
                        />
                    </InputField>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        disabled={!isValid}
                        loading={createPrompt.isPending}
                        data-testid="save-command-button"
                        data-tracking-id="create-agent-command-new-command-modal"
                    >
                        Create Command
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
